import { motion } from "framer-motion";
import { Target, Heart, ArrowRight, Award } from "lucide-react";
import { Link } from "react-router-dom";
import InstagramWidget from "./InstagramWidget";
import FacebookWidget from "./FacebookWidget";
import TikTokWidget from "./TikTokWidget";
import YouTubeWidget from "./YouTubeWidget";

export default function OurStory() {
  return (
    <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: "'Lora', Georgia, 'Times New Roman', serif" }}>
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#082f6d]/10 via-[#082f6d]/5 to-transparent py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-extrabold text-[#082f6d] tracking-tight mb-6"
          >
            The Story Behind DallasDesiScoop
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed"
          >
            From a simple passion project to a community platform for the Dallas-Fort Worth Desi community.
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-12 items-start">
          {/* Left Column: Founders Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full md:col-span-1 bg-white rounded-2xl p-6 shadow-sm border border-slate-100 md:sticky md:top-24"
          >
            <div className="flex flex-col items-center text-center">
              <img
                src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1789608141583-4sie39.jpg"
                alt="Abhijit & Anupama Jadhav, Founders of DallasDesiScoop"
                className="w-full max-w-[280px] md:max-w-full aspect-[3/4] object-cover rounded-2xl shadow-md mb-5"
              />
<h3 className="text-base font-bold text-[#082f6d]">Abhijit & Anupama Jadhav</h3>
              <p className="text-sm text-[#51ba4f] font-semibold mt-1">Founders</p>
              <p className="text-xs text-gray-400 mt-0.5">DallasDesiScoop</p>
              
              <div className="w-full border-t border-slate-100 my-6" />
              
              <div className="space-y-4 text-left w-full">
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#f59e0b] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Our Passion</h4>
                    <p className="text-xs text-gray-500 mt-1">Connecting the local South Asian community</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#51ba4f] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Our Mission</h4>
                    <p className="text-xs text-gray-500 mt-1">Shining a spotlight on local businesses and events</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Detailed Story */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full md:col-span-2 space-y-6 text-gray-700 leading-relaxed"
          >
            <h2 className="text-xl font-bold text-[#082f6d] mb-4">Our Story</h2>
            
            <p className="text-base text-gray-700">
              Dallas Desi Scoop was born from a simple idea: to create one place where the South Asian community in the Dallas-Fort Worth area could easily discover everything happening around them. As people who have always loved connecting with others, exploring local businesses, restaurants, cultural events, and community initiatives, we realized there wasn't a single platform bringing it all together.
            </p>
            
            <p className="text-base text-gray-700">
              What started as a passion project on social media has grown into a trusted community platform followed by thousands of people looking for the latest events, hidden food gems, local businesses, and inspiring stories. Along the way, we've had the privilege of collaborating with entrepreneurs, nonprofits, artists, and community leaders, helping shine a spotlight on their work and connect them with a wider audience.
            </p>

            <div className="border-t border-slate-100 my-8 pt-8 space-y-6">
              <div>
                <p className="text-lg font-bold text-[#082f6d] mb-2">
                  Discover • Explore • Connect
                </p>
                <p className="text-base text-gray-700">
                  DallasDesiScoop is our way of bringing people, businesses, events, and stories together in one place.
                </p>
              </div>

              <div className="bg-[#082f6d]/5 border border-[#082f6d]/10 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-[#f59e0b]" />
                  <p className="text-lg font-bold text-[#082f6d]">
                    Featured in VoyageDallas
                  </p>
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  Our journey with DallasDesiScoop was featured by VoyageDallas in its Rising Stars series.
                </p>
                <a 
                  href="https://voyagedallas.com/interview/rising-stars-meet-anubhi-jadhav-of-dallas-fort-worth/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#51ba4f] hover:bg-[#51ba4f]/90 text-white font-semibold text-sm rounded-lg transition-all duration-200"
                >
                  Read the full interview
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <div className="pt-6 space-y-4">
                <p className="text-lg font-bold text-[#082f6d]">
                  What You'll Find on DallasDesiScoop
                </p>
                <p className="text-base text-gray-700">
                  DallasDesiScoop brings together local discoveries and resources for the growing DFW Desi community. From finding a new restaurant or grocery store to discovering an upcoming concert, festival, temple, local business, or service, our goal is to make it easier to find what you're looking for in one place.
                </p>
                <p className="text-base text-gray-700">
                  Explore Indian, Pakistani, South Asian, and Desi restaurants, grocery stores, shopping, desserts, temples, events, festivals, concerts, photographers, caterers, party venues, artists, businesses, professionals, and community services across Dallas–Fort Worth.
                </p>
              </div>

              <div className="pt-6 space-y-4">
                <p className="text-lg font-bold text-[#082f6d]">
                  Connect & Collaborate
                </p>
                <p className="text-base text-gray-700">
                  There are many ways to work with and support DallasDesiScoop. We welcome collaborations with local businesses, restaurants, entrepreneurs, nonprofits, artists, and event organizers who want to reach and engage the South Asian community in the Dallas-Fort Worth area.
                </p>
                <p className="text-base text-gray-700">
                  Whether it's promoting an event, showcasing a business, sharing a community initiative, or partnering on campaigns, we're always excited to build meaningful relationships.
                </p>
                <p className="text-base text-gray-700">
                  Community members can also support us by following our platforms, sharing our content, recommending local stories, and helping us spread the word. Our mission is to grow together as a community, and every collaboration helps us create a stronger platform that benefits everyone.
                </p>
              </div>
            </div>

            {/* Follow Us — social widgets moved from the home page */}
            <div className="pt-6">
              <p className="text-lg font-bold text-[#082f6d] mb-4">
                Follow DallasDesiScoop
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InstagramWidget />
                <FacebookWidget />
                <TikTokWidget />
                <YouTubeWidget />
              </div>
            </div>

            <div className="pt-4">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#082f6d] hover:bg-[#082f6d]/90 text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Explore the Directory
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
